/**
 * Landing page component for AskAsper
 * Shown when user first arrives (no messages yet)
 */

import React from 'react';
import { AgenticMainLayout, QuickAction } from 'asper_design_library';
import type { SidebarThread } from '../services/chatHistoryService';
import { type QuestionBankCategory, toQuestionBankApiFormat } from '../services/questionBankService';
import type { LookupItem } from '../services/lookupColumnsService';
import type { LookupDimensionGroup } from '../utils/lookupDimensionGroups';
import type { UserProfile, CustomQuestionDisplay } from '../types';

interface LandingPageProps {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    onQuickActionClick?: (action: QuickAction) => void;
    isLoading?: boolean;
    /** List of conversation threads to display in the sidebar */
    threads?: SidebarThread[];
    /** Whether sidebar threads are loading */
    sidebarThreadsLoading?: boolean;
    /** Currently active thread ID */
    activeThreadId?: string;
    /** User profile for sidebar (overrides userName for display/avatar) */
    user?: UserProfile;
    /** Whether the sidebar is in collapsed (icon-only) mode when expanded */
    sidebarDefaultCollapsed?: boolean;
    /** Whether to hide the sidebar header (logo and title) */
    sidebarHideHeader?: boolean;
    onNewChat?: () => void;
    onThreadSelect?: (threadId: string) => void;
    onThreadEdit?: (threadId: string, newName?: string) => void | Promise<void>;
    onThreadDelete?: (threadId: string) => void | Promise<void>;
    /** Custom questions for the question bank */
    customQuestions?: CustomQuestionDisplay[];
    /** Question bank categories (bucketed by category from API) */
    questionBankCategories?: QuestionBankCategory[];
    onAddCustomQuestion?: (question: { id: string; text: string }) => void | Promise<void>;
    onEditCustomQuestion?: (
        question: { id?: string | number; text?: string; QuestionID?: number; Question?: string },
    ) => void | Promise<void>;
    onDeleteCustomQuestion?: (questionId: string) => void | Promise<void>;
    /** All available lookup items for the `/` slash-command dropdown (eager-loaded by the parent
     *  with a 1-week localStorage cache; the page render is gated on this being ready). */
    lookupItems?: LookupItem[];
    /** Lookups the user has already inserted as `/entity_name` tokens in the input. */
    selectedLookups?: LookupItem[];
    /** Called when the selected-lookups list changes (selection added or token removed). */
    onLookupsChange?: (next: LookupItem[]) => void;
    /**
     * Optional Product / Market buckets for `/`. When set, do not also pass a conflicting
     * flat `lookupCategories` — the library prefers categories and would ignore groups.
     */
    lookupDimensionGroups?: LookupDimensionGroup[];
    /** Mixpanel / analytics — passed through to AgenticMainLayout as `onTrackEvent` */
    onTrackEvent?: (eventName: string, eventProperty?: object) => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
    searchValue = '',
    onSearchChange,
    onSearchSubmit,
    onQuickActionClick,
    isLoading = false,
    threads = [],
    sidebarThreadsLoading = false,
    activeThreadId,
    user,
    sidebarDefaultCollapsed = false,
    sidebarHideHeader = false,
    onNewChat,
    onThreadSelect,
    onThreadEdit,
    onThreadDelete,
    customQuestions,
    questionBankCategories,
    onAddCustomQuestion,
    onEditCustomQuestion,
    onDeleteCustomQuestion,
    lookupItems,
    selectedLookups,
    onLookupsChange,
    lookupDimensionGroups,
    onTrackEvent,
}) => {
    const handleQuickActionClick = (action: QuickAction) => {
        if (action.fullQuestion && onSearchChange && onSearchSubmit) {
            onSearchChange(action.fullQuestion);
            onSearchSubmit(action.fullQuestion);
        }
        onQuickActionClick?.(action);
    };

    const layoutProps = {
        quickActions: [],
        searchValue,
        isLoading,
        onSearchChange,
        onSearchSubmit,
        onQuickActionClick: handleQuickActionClick,
        threads,
        threadsLoading: sidebarThreadsLoading,
        activeThreadId,
        userName: user?.name,
        sidebarDefaultCollapsed,
        sidebarHideHeader,
        onNewChat,
        onThreadSelect,
        onThreadEdit,
        onThreadDelete,
        disableNavbar: true,
        customQuestions,
        questionBankCategories: questionBankCategories?.length
            ? toQuestionBankApiFormat(questionBankCategories)
            : undefined,
        onSaveAsCustom: (value: string) => onAddCustomQuestion?.({ id: '', text: value }),
        onEditCustomQuestion,
        onDeleteCustomQuestion,
        lookupItems,
        selectedLookups,
        onLookupsChange,
        // Dimension groups only — never pass lookupCategories alongside (library prefers categories).
        lookupDimensionGroups,
        onTrackEvent,
    };
    return <AgenticMainLayout {...(layoutProps as any)} />;
};
